import type { SorteDeLien } from '@nextilt/commun';

/**
 * Les mots de « Déplacer » et de « Lier » : le choix de la destination, la boucle refusée, et ce
 * qu'un lien va produire.
 */

/**
 * « Déplacer » depuis la fiche (chantier `deplacer`, ticket 02, décisions d'Igor du 2026-09-29
 * et du 2026-10-01) : le bouton de la tête, le choix de la destination au-dessus de la fiche,
 * et le panneau de la boucle réduit à une issue quand le rangement en fermerait une.
 */
export const DEPLACER = 'Déplacer';
export const NE_PAS_DEPLACER = 'Ne pas déplacer';
export function deplacerLaBulle(titre: string): string {
  return `Déplacer « ${titre} »`;
}
/** Un refus autre que la boucle (dans sa propre branche, trop profond, disparue). */
export const DEPLACEMENT_REFUSE = 'Elle ne peut pas aller là. Rien n’a bougé.';
export const DEPLACEMENT_ECHOUE = 'Ça n’est pas passé. Rien n’a bougé, réessaie dans un instant.';
export const MOTS_BOUCLE_AU_DEPLACEMENT = {
  aria: 'ce rangement refermerait une boucle',
  explication: (titre: string) =>
    `Ranger « ${titre} » ici refermerait le cercle : chacune attendrait la suivante, et `,
  annotation: ['Ce que poserait', 'le rangement'] as const,
  consigne: 'Une bulle attend ce qu’elle contient : ce rangement fermerait la boucle',
  pied: 'Rien n’a bougé : elle reste où elle était.',
};

/*
 * « Ce que ce lien va produire », sous la recherche de « Lier » (chantier `bugs`, ticket 02,
 * décisions d'Igor du 2026-10-01). Le choix de la phrase : `ceQueProduitLeLien.ts`.
 * `lienTantQue` est suivi, à l'écran, de « les deux avancent ensemble » en gras.
 */
export const CE_QUE_CE_LIEN_VA_PRODUIRE = 'Ce que ce lien va produire';
export const LES_DEUX_AVANCENT_ENSEMBLE = 'les deux avancent ensemble';
export function lienTantQue(attendue: string, bloquee: string): string {
  return `Tant que « ${attendue} » n’est pas acquise, « ${bloquee} » attend. Le jour où tu la coches, `;
}
/**
 * Les jours du monde que le lien fait courir, accordés : « le jour » pour un seul (la phrase entière
 * passe au singulier, ce que `delaiEnMots` ne sait pas dire), « les 10 jours » à partir de deux.
 * Zéro : rien à courir, la phrase ne se dit pas.
 */
export function lienJoursDuMonde(jours: number): string {
  if (jours <= 0) return '';
  if (jours === 1) return ' Et le jour du monde commence enfin à courir.';
  return ` Et les ${jours} jours du monde commencent enfin à courir.`;
}
export function lienDejaFait(attendue: string): string {
  return `« ${attendue} » est déjà faite : ce lien ne retient rien aujourd’hui. Il comptera si tu la remets à faire.`;
}
export function lienGardeCommeIdee(attendue: string): string {
  return `« ${attendue} » est gardée comme idée : ce lien ne retient rien aujourd’hui. Il comptera si elle redevient une tâche.`;
}

/*
 * « Lier », sur la carte (chantier liens-entre-bulles, ticket 04, décisions 2, 3 et 11 du cadrage,
 * « Les mots » de la spec du chantier) : le bouton et la touche J, le bandeau, la sorte et son sens
 * dit en clair depuis la première bulle, et ce que dit chaque clic.
 */
export const LIER = 'Lier';
export const LIER_TITRE = 'Relier des bulles entre elles, ou la touche J';
/** Les trois sortes du bandeau, dans son ordre. */
export const MOTS_DES_SORTES_A_LIER: Record<SorteDeLien, string> = {
  attend: 'Attend',
  sert_a: 'Sert à',
  touche_a: 'Touche à',
};
export const CLIQUE_LA_PREMIERE = 'Clique la première bulle.';
export const PUIS_LA_SORTE = 'Puis choisis la sorte de lien, et clique les bulles à lui relier, une à une.';
/** La sorte déjà choisie par une des trois flèches du bouton « Lier ». */
export const PUIS_LES_BULLES = 'Puis clique les bulles à lui relier, une à une.';
export function laPremiere(titre: string): string {
  return `La première : « ${titre} »`;
}
/** Le sens, depuis la première : ce que deviendra chaque bulle cliquée. */
export function sensDuLien(titre: string, sorte: SorteDeLien | null): string {
  if (sorte === null) return 'Choisis la sorte de lien.';
  const verbe = { attend: 'attend', sert_a: 'sert à', touche_a: 'touche à' }[sorte];
  return `« ${titre} » ${verbe} chaque bulle que tu cliques.`;
}
/** Combien de bulles ont été reliées depuis le début de « Lier ». */
export function bullesReliees(n: number): string {
  return n <= 1 ? `${n} reliée` : `${n} reliées`;
}
export const RELIEE = 'Reliée.';
export function lienChange(sorte: SorteDeLien): string {
  return `Lien changé : ${MOTS_DES_SORTES_A_LIER[sorte]}.`;
}
export const DEJA_RELIEE = 'Déjà reliée ainsi : rien n’a changé.';
export const PAS_A_ELLE_MEME = 'Une bulle ne se lie pas à elle-même.';
/** Recliquer une paire liée dans l'autre sens ne retourne rien (décision 11 du cadrage). */
export const LIEN_DANS_L_AUTRE_SENS = 'Ce lien va dans l’autre sens : pour le retourner, clique son trait, puis ⇄.';
export const LIEN_BOUCLERAIT = 'Rien n’a été posé : ce lien fermerait une boucle.';
export const LIEN_PAS_POSE = 'Le lien n’a pas pu se poser. Réessaie.';
export const FINI = 'Fini';
export const TOUCHE_ECHAP_EN_CLAIR = 'Touche échap';

/*
 * Le cadenas de la Carte (chantier deplacer-les-bulles, ticket 03, ADR 0016) : son nom au survol,
 * le même fermé ou ouvert (l'état se dit par son dessin et `aria-pressed`).
 */
export const DEPLACER_LES_BULLES = 'Déplacer les bulles';

/** Dans le « ⋯ » d'une bulle déplacée, sur la Carte (ticket 04) : sa place gardée de cette profondeur s'efface. */
export const REMETTRE_A_SA_PLACE = 'Remettre à sa place';
